import { create } from 'zustand';
import { extractJwtTokenFromUrl, isKnownHostUrl, removeTokenFromUrl } from 'utils/urlUtils';
import { KNOWN_HOSTS } from 'shared/config/firebase';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createLogger } from 'shared/logger';

const log = createLogger('JwtAuthStore');

interface JwtAuthState {
  jwtToken: string | null; // Original token from URL (private, for re-verification)
  jwtSession: string | null; // Session ID from /verify response (used for API calls)
  isJwtAuthenticated: boolean;
  isVerified: boolean;
  isInKnownHost: boolean;
  shouldUseJwt: boolean; // Flag to indicate whether JWT auth should be used instead of Firebase

  // Actions
  setJwtToken: (token: string | null) => void;
  setJwtSession: (session: string | null) => void;
  setIsVerified: (isVerified: boolean) => void;
  initializeFromUrl: () => void;
  clearJwtAuth: () => void;
}

// Get initial token from sessionStorage if available
const getStoredToken = (): string | null => {
  try {
    const storedAuth = sessionStorage.getItem('jwt-auth');
    if (storedAuth) {
      const parsedAuth = JSON.parse(storedAuth);
      return parsedAuth.state.jwtToken;
    }
  } catch (error) {
    log.error('failed to read stored JWT token', { error });
  }
  return null;
};

// Get stored session from sessionStorage if available
const getStoredSession = (): string | null => {
  try {
    const storedAuth = sessionStorage.getItem('jwt-auth');
    if (storedAuth) {
      const parsedAuth = JSON.parse(storedAuth);
      return parsedAuth.state.jwtSession;
    }
  } catch (error) {
    log.error('failed to read stored JWT session', { error });
  }
  return null;
};

const jwtAuthStore = create<JwtAuthState>()(
  persist(
    (set, get) => ({
      jwtToken: getStoredToken(),
      jwtSession: getStoredSession(),
      isJwtAuthenticated: !!getStoredSession(),
      isVerified: !!getStoredSession(), // Assume stored session is already verified
      isInKnownHost: isKnownHostUrl(KNOWN_HOSTS),
      shouldUseJwt: !!getStoredSession() && isKnownHostUrl(KNOWN_HOSTS),

      setJwtToken: (token: string | null) => {
        set({
          jwtToken: token,
        });
      },

      setJwtSession: (session: string | null) => {
        const isJwtAuthenticated = !!session;
        set({
          jwtSession: session,
          isJwtAuthenticated,
          // Only set shouldUseJwt true if we have a session and are in a known host
          shouldUseJwt: isJwtAuthenticated && get().isInKnownHost
        });
      },

      setIsVerified: (isVerified: boolean) => {
        set({
          isVerified,
          // Update shouldUseJwt based on verification status and known host
          shouldUseJwt: isVerified && get().isInKnownHost && !!get().jwtSession
        });
      },

      initializeFromUrl: () => {
        // Get token from URL
        const token = extractJwtTokenFromUrl();

        // Check if we're in a known host
        const isInKnownHost = isKnownHostUrl(KNOWN_HOSTS);

        // If we have a token in the URL, use it
        if (token) {
          set({
            jwtToken: token,
            isJwtAuthenticated: true,
            isInKnownHost,
            isVerified: false,
            // Initially, we'll set shouldUseJwt if we have a token and are in a known host
            // This will be updated after verification
            shouldUseJwt: isInKnownHost
          });
          removeTokenFromUrl();
        }
        // Otherwise, keep using the persisted token if available
        else if (!get().jwtToken) {
          // Only update isInKnownHost if no token already set
          set({
            isInKnownHost
          });
        }
      },
      
      clearJwtAuth: () => {
        set({
          jwtToken: null,
          jwtSession: null,
          isJwtAuthenticated: false,
          isVerified: false,
          shouldUseJwt: false
        });
      }
    }),
    {
      name: 'jwt-auth',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist these keys
      partialize: (state) => ({
        jwtToken: state.jwtToken,
        jwtSession: state.jwtSession,
        isJwtAuthenticated: state.isJwtAuthenticated,
        isVerified: state.isVerified,
        shouldUseJwt: state.shouldUseJwt
      }),
    }
  )
);

export default jwtAuthStore; 